import React from "react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
export const STATUS_CLS = { paid: "bg-emerald-50 text-emerald-700 border-emerald-200", partial: "bg-amber-50 text-amber-800 border-amber-200", unpaid: "bg-rose-50 text-rose-700 border-rose-200", cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200" };

export function Field({ label, value, onChange, tid, type = "text", full, placeholder }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">{label}</Label>
      <Input data-testid={tid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-none" />
    </div>
  );
}

export function StatusBadge({ s }) {
  return <span data-testid="bill-status" className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${STATUS_CLS[s] || STATUS_CLS.unpaid}`}>{s === "partial" ? "Partly paid" : s}</span>;
}

// ---------------- Profile ----------------
export function BillingProfile() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef();
  const load = () => api.get("/billing/profile").then(r => setP(r.data));
  useEffect(() => { load(); }, []);
  if (!p) return <div className="text-sm text-zinc-500">Loading…</div>;
  const u = (k) => (v) => setP({ ...p, [k]: v });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch("/billing/profile", p); setP(data); toast.success("Business profile saved"); }
    catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/billing/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setP(data); toast.success("Logo uploaded"); }
    catch (e) { toast.error("Upload failed", { description: e.response?.data?.detail || e.message }); }
  };
  const logoSrc = p.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}` : "";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6" data-testid="billing-profile">
      <div className="border border-zinc-200 bg-white p-4 flex flex-col items-center gap-3 self-start">
        <div className="w-32 h-32 border border-zinc-200 bg-zinc-50 grid place-items-center overflow-hidden">
          {logoSrc ? <img src={logoSrc} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-zinc-400 uppercase tracking-widest">Your logo</span>}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="billing-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
        <Button data-testid="billing-logo-btn" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-none border-zinc-300 w-full">Upload logo</Button>
        <div className="text-[10px] text-zinc-500 text-center">Printed on every bill you create · PNG/JPG ≤ 3 MB</div>
      </div>
      <div className="border border-zinc-200 bg-white p-5 grid grid-cols-2 gap-3">
        <Field label="Business name" value={p.business_name} onChange={u("business_name")} tid="bp-name" full />
        <Field label="Address" value={p.address} onChange={u("address")} tid="bp-address" full />
        <Field label="City" value={p.city} onChange={u("city")} tid="bp-city" />
        <Field label="State" value={p.state} onChange={u("state")} tid="bp-state" />
        <Field label="State code (GST)" value={p.state_code} onChange={u("state_code")} tid="bp-state-code" placeholder="e.g. 27" />
        <Field label="Pincode" value={p.pincode} onChange={u("pincode")} tid="bp-pincode" />
        <Field label="GSTIN" value={p.gstin} onChange={u("gstin")} tid="bp-gstin" />
        <Field label="PAN" value={p.pan} onChange={u("pan")} tid="bp-pan" />
        <Field label="Phone" value={p.phone} onChange={u("phone")} tid="bp-phone" />
        <Field label="Email" value={p.email} onChange={u("email")} tid="bp-email" />
        <Field label="Invoice prefix" value={p.invoice_prefix} onChange={u("invoice_prefix")} tid="bp-prefix" placeholder="INV" />
        <Field label="Signature name" value={p.signature_name} onChange={u("signature_name")} tid="bp-sign" />
        <Field label="Bank name" value={p.bank_name} onChange={u("bank_name")} tid="bp-bank" />
        <Field label="Account number" value={p.bank_account} onChange={u("bank_account")} tid="bp-account" />
        <Field label="IFSC" value={p.bank_ifsc} onChange={u("bank_ifsc")} tid="bp-ifsc" />
        <Field label="UPI ID" value={p.upi_id} onChange={u("upi_id")} tid="bp-upi" />
        <div className="col-span-2">
          <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Default terms</Label>
          <Textarea data-testid="bp-terms" rows={2} value={p.terms || ""} onChange={(e) => u("terms")(e.target.value)} />
        </div>
        <div className="col-span-2 flex justify-end">
          <Button data-testid="bp-save" onClick={save} disabled={busy} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">{busy ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Customers ----------------
const emptyCust = { name: "", gstin: "", address: "", state_code: "", phone: "", email: "" };
export function BillingCustomers({ onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCust);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/billing/customers").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editId) await api.patch(`/billing/customers/${editId}`, form); else await api.post("/billing/customers", form);
      toast.success(editId ? "Customer updated" : "Customer added"); setOpen(false); setForm(emptyCust); setEditId(null); load(); onChanged?.();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const remove = async (c) => {
    try { await api.delete(`/billing/customers/${c.id}`); toast.success("Customer removed"); load(); }
    catch (e) { toast.error("Cannot delete", { description: e.response?.data?.detail || e.message }); }
  };
  const u = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-3" data-testid="billing-customers">
      <div className="flex justify-end">
        <Button data-testid="cust-add-btn" onClick={() => { setForm(emptyCust); setEditId(null); setOpen(true); }} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">+ Add customer</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(c => (
          <article key={c.id} data-testid={`cust-card-${c.id}`} className="border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm truncate">{c.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{c.gstin || "No GSTIN"}{c.address ? ` · ${c.address}` : ""}</div>
                <div className="text-[11px] text-zinc-500 truncate">{[c.phone, c.email].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`cust-portal-${c.id}`} onClick={async () => { const { data } = await api.get(`/bizx/customers/${c.id}/portal-token`); const url = `${window.location.origin}/c/${data.token}`; const phone = (c.phone || "").replace(/\D/g, ""); window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Dear ${c.name}, view all your bills & dues and pay via UPI here: ${url}`)}`, "_blank", "noopener"); }} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white">Portal link</button>
                <button data-testid={`cust-edit-${c.id}`} onClick={() => { setForm({ ...emptyCust, ...c }); setEditId(c.id); setOpen(true); }} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-zinc-200 hover:bg-zinc-900 hover:text-white">Edit</button>
                {!c.invoice_count ? <button data-testid={`cust-del-${c.id}`} onClick={() => remove(c)} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-zinc-200 hover:bg-rose-600 hover:text-white">Delete</button> : null}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center border-t border-zinc-100 pt-3">
              <div><div className="text-[9px] uppercase tracking-wider text-zinc-500">Billed</div><div className="tabular text-xs font-semibold">{inr(c.billed)}</div></div>
              <div><div className="text-[9px] uppercase tracking-wider text-zinc-500">Received</div><div className="tabular text-xs font-semibold text-emerald-700">{inr(c.paid)}</div></div>
              <div><div className="text-[9px] uppercase tracking-wider text-zinc-500">Outstanding</div><div data-testid={`cust-outstanding-${c.id}`} className={`tabular text-xs font-semibold ${c.outstanding > 0 ? "text-rose-700" : ""}`}>{inr(c.outstanding)}</div></div>
            </div>
          </article>
        ))}
        {!items.length && <div className="md:col-span-2 border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500">No customers yet. Add your first customer to start billing.</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight text-xl">{editId ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" value={form.name} onChange={u("name")} tid="cust-name" full />
            <Field label="GSTIN (optional)" value={form.gstin} onChange={u("gstin")} tid="cust-gstin" />
            <Field label="State code" value={form.state_code} onChange={u("state_code")} tid="cust-state" placeholder="auto from GSTIN" />
            <Field label="Address" value={form.address} onChange={u("address")} tid="cust-address" full />
            <Field label="Phone" value={form.phone} onChange={u("phone")} tid="cust-phone" />
            <Field label="Email" value={form.email} onChange={u("email")} tid="cust-email" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="cust-save" onClick={save} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
